import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';

import { CategoriesForMenu } from 'shared/constants/products';

import { CategoriesCarousel } from 'src/components/carousel-v2/types/categories-carousel';
import CategoryTile from 'src/components/category-tile';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';

import { selectField } from '../fields/widgets/select-field';
import { interpolateFromPuck } from '../interpolation';

/**
 * An **author-chosen** carousel of navigation tiles: which categories or collections
 * appear, and in what order.
 *
 * **Not a Product Carousel type, deliberately.** Every type in that block is a carousel OF PRODUCTS
 * with a different filter -- `CategoryCarousel` calls `useDispensaryProducts({ filters: { category } })`
 * and renders `<ProductCarousel>`, so "Popular Category" means products *within* a category. Here
 * the items ARE the categories: navigation tiles with a destination each, not products with an
 * add-to-cart. Adding this as a Product Carousel type would put a non-product carousel inside a
 * block whose every other type returns products and whose heading and `isConfigured` machinery
 * assumes it.
 *
 * **Complements `categoriesNavConfig`, which it must not replace.** That block is `fields: {}` /
 * `defaultProps: {}`, so it mirrors whatever categories the store has. This is the third
 * store-mirror-versus-authored pair in this codebase (Image Banner Rotation / Slideshow, Categories /
 * this Carousel, and the Text Banner's store fallback), so it is an established pattern rather than a
 * coincidence.
 *
 * The palette label names the authored destination behavior so authors do not confuse this block
 * with Product Carousel, whose tiles are products rather than links.
 *
 * Items come from `useDispensaryCategories`, which already returns categories and collections in one
 * shape (`{ key, label, value, slug?, customCategory? }`), already access-filtered and store-ordered.
 * Reusing that rather than querying separately means an author cannot select a collection a shopper
 * is not entitled to see: anything the hook withholds is simply not available to render.
 */

/** One row of the items array. Puck's array field stores objects, not bare strings. */
export type NavItem = { value: string };

export type NavCarouselProps = {
  itemType: string;
  items: NavItem[];
  heading: string;
  showHeading: string;
};

// Stable reference. A fresh `[]` as a destructuring default is a new value on every render.
const EMPTY_ITEMS: NavItem[] = [];

/**
 * Subcategories are deliberately absent, and it is not an oversight.
 *
 * Tiles are resolved against `useDispensaryCategories`, which returns only top-level categories and
 * collections: it maps its filter data with `.map(({ category }) => category)`
 * (`use-dispensary-categories.js:114`), discarding the subcategory. `CategoryTile`'s destination
 * helper `useGetCustomCategoryLink(customCategory, slug, key)` has no subcategory parameter either,
 * so a subcategory tile would have nothing to resolve and nowhere to link.
 *
 * Offering it would have let an author pick valid-looking subcategories, save, and get a permanently
 * empty carousel -- a dead option is worse than an absent one. Adding it needs a resolution source
 * and a destination for subcategory tiles, which is a separate piece of work.
 */
const ITEM_TYPE_OPTIONS = [
  { label: 'Categories', value: 'categories' },
  { label: 'Collections', value: 'collections' },
] as const;

const SHOW_HEADING_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
] as const;

/**
 * The option list for a given item type.
 *
 * Categories come from the canonical static constants, so the CMS cannot drift from the menu's own
 * vocabulary. Subcategories are deliberately excluded here -- see the note above `ITEM_TYPE_OPTIONS`.
 * Collections are per-store live data, so they are not offered here: the picker below resolves them
 * from the dispensary at render time instead.
 */
export function itemOptionsFor(itemType: string): { label: string; value: string }[] {
  if (itemType === 'categories') {
    return CategoriesForMenu.map(({ label, value }) => ({ label, value }));
  }
  return [];
}

/**
 * Keeps only stored item values that are valid for the current item type, in the author's order.
 *
 * Saved page data is unvalidated JSON, and switching item type leaves the previous type's values
 * behind -- a collection slug must not survive a switch to Categories. Same class of staleness as
 * `ProductCarousel`'s subcategory, handled the same way: drop rather than render something the
 * author is no longer asserting.
 */
export function validItems(itemType: string, items: unknown): string[] {
  if (!Array.isArray(items)) {
    return [];
  }
  // Rows are `{ value }`, but a hand-edited payload may hold bare strings; accept both rather than
  // discarding an author's list over its shape.
  // Deduplicated, keeping the FIRST occurrence so the author's order is preserved. Tiles are keyed
  // by the stored value, so a repeat would give React two children with the same key in one list --
  // and the same tile twice in a row is not a thing an author means to publish. Saved JSON is
  // hand-editable, so the picker cannot be the only place this is prevented.
  const seen = new Set<string>();
  const strings = items.reduce<string[]>((acc, item) => {
    const value = typeof item === 'string' ? item : (item as NavItem | undefined)?.value;
    if (typeof value === 'string' && value !== '' && !seen.has(value)) {
      seen.add(value);
      acc.push(value);
    }
    return acc;
  }, []);
  // Collections are per-store, so their validity can only be judged against a resolved dispensary,
  // which happens in the renderer. Static types are checked against their canonical list here.
  if (itemType === 'collections') {
    return strings;
  }
  const allowed = new Set(itemOptionsFor(itemType).map((option) => option.value));
  return strings.filter((item) => allowed.has(item));
}

type ResolvedCategory = {
  key?: string;
  label?: string;
  value?: string;
  slug?: string;
  customCategory?: boolean;
};

/**
 * Matches an author's stored value against the store's resolved list.
 *
 * Categories are matched on `value`, collections on `slug` -- the same identifiers the CMS store
 * elsewhere uses, and the reason a collection's slug rather than its UUID is what gets saved.
 */
function findResolved(stored: string, resolved: ResolvedCategory[], itemType: string): ResolvedCategory | undefined {
  if (itemType === 'collections') {
    return resolved.find((category) => category.customCategory === true && category.slug === stored);
  }
  return resolved.find((category) => category.customCategory !== true && category.value === stored);
}

function NavCarouselRender({
  itemType = '',
  items = EMPTY_ITEMS,
  heading = '',
  showHeading = 'true',
  puck,
}: NavCarouselProps & { puck: { isEditing: boolean; metadata?: Record<string, unknown> } }): JSX.Element {
  const { dispensary } = useDispensary();
  // Typed as required by the hook's .d.ts, but it really can arrive undefined -- the published
  // renderer has no error boundary, so this guard is load-bearing rather than defensive.
  const { categories } = useDispensaryCategories() as { categories?: ResolvedCategory[] };

  const chosen = validItems(itemType, items);
  const resolvedTitle = showHeading === 'false' ? '' : interpolateFromPuck(heading, puck);

  // CategoryTile reads the dispensary for its photo, and useDispensaryCategories needs one to
  // resolve collections at all, so neither can render before the dispensary loads.
  if (!dispensary) {
    return <></>;
  }

  if (itemType === '' || chosen.length === 0) {
    if (!puck.isEditing) {
      return <></>;
    }
    return (
      <div
        data-testid='cms-nav-carousel-placeholder'
        style={{
          padding: 24,
          border: '1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd)',
          borderRadius: 'var(--dcm-cms-placeholder-radius, 8px)',
          background: 'var(--dcm-cms-placeholder-bg, #f0f2f4)',
          color: 'var(--dcm-cms-placeholder-color, #646d72)',
          fontSize: 14,
        }}
      >
        {itemType === '' ? 'Choose what this carousel shows in the settings panel.' : 'Choose at least one item.'}
      </div>
    );
  }

  // Author order is the point of this block, so the stored list drives iteration -- not the store's
  // own ordering, which `useDispensaryCategories` has already applied to `categories`.
  const tiles = chosen.reduce<JSX.Element[]>((acc, stored) => {
    const resolved = findResolved(stored, categories ?? [], itemType);
    // An item the store does not have (removed category, collection on another store, or one this
    // shopper cannot see) is skipped rather than rendered as a dead tile.
    if (resolved) {
      acc.push(
        <div key={stored} data-testid='cms-nav-carousel-tile' data-item={stored}>
          <CategoryTile category={resolved} dispensary={dispensary} disableImageAlt />
        </div>
      );
    }
    return acc;
  }, []);

  if (tiles.length === 0) {
    if (!puck.isEditing) {
      return <></>;
    }
    return (
      <div
        data-testid='cms-nav-carousel-empty'
        style={{ padding: 24, fontSize: 14, color: 'var(--dcm-cms-placeholder-color, #646d72)' }}
      >
        None of the chosen items exist for this store, so nothing will appear on the published page.
      </div>
    );
  }

  return (
    <div data-testid='cms-nav-carousel' data-order={chosen.join(',')}>
      <CategoriesCarousel title={resolvedTitle}>{tiles}</CategoriesCarousel>
    </div>
  );
}

export const navCarouselConfig: ComponentConfig<NavCarouselProps> = {
  label: 'Link Carousel',
  ai: {
    instructions:
      'A carousel of navigation tiles the AUTHOR chooses: categories or collections, in a chosen order. Not the same as "Categories", which shows every category the store has and cannot be edited from a page. Not a product carousel either -- use Product Carousel when the items should be products.',
  },
  fields: {
    itemType: selectField(
      'Show',
      // A blank first option: the item type drives every other decision, so a freshly dropped block
      // asks rather than silently picking one. It must be a real option, or the select would display
      // an entry the stored value does not match.
      [{ label: 'Select what to show…', value: '' }, ...ITEM_TYPE_OPTIONS],
      'What kind of thing the tiles link to. Everything else follows from this, so always set it.'
    ),
    // Rebuilt per item type by resolveFields; the static entry exists to satisfy TypeScript.
    items: {
      type: 'custom',
      label: 'Items',
      render: () => <></>,
      ai: { exclude: true },
    },
    heading: {
      type: 'text',
      label: 'Heading',
      ai: { instructions: 'Optional title above the carousel. Supports {{name}} and {{address}} placeholders.' },
    },
    showHeading: selectField(
      'Show heading',
      SHOW_HEADING_OPTIONS,
      'Set to No to render the carousel with no heading, which differs from leaving Heading empty.'
    ),
  },
  defaultProps: {
    itemType: '',
    items: EMPTY_ITEMS,
    heading: '',
    showHeading: 'true',
  },
  /**
   * Drops stored items that are not valid for the current item type, AND normalizes bare strings to
   * `{ value }` rows -- both are `resolveData`'s job because `resolveFields` rebuilds the item
   * *field* when the type changes but cannot touch stored *props*.
   *
   * Dropping stale items: without it, a collection slug would survive a switch to Categories and the
   * panel would assert a selection the renderer ignores. Mirrors `ProductCarousel`'s stale-subcategory
   * handling.
   *
   * **A genuine itemType SWITCH always clears items, in either direction.** `validItems` cannot do
   * this alone: it filters categories against the static canonical list, but collections have no
   * static list to filter against (only a resolved dispensary can validate a slug, and that
   * resolution happens in the renderer, not here) -- so without this check, switching Categories ->
   * Collections left the OLD category values sitting untouched under "Collections (in order)". They
   * are never real collection slugs, so none of them resolve at render time, and the canvas silently
   * shows nothing even though the panel still looks like it kept a real selection. Detecting the
   * switch via Puck's own `changed`/`lastData` params generalises the existing categories-only
   * static-list filtering into one symmetric rule -- "switching type always starts you fresh" --
   * rather than leaving collections as a silent asymmetric exception. Found in review of
   * ECOMM-13238.
   *
   * This only fires for an ACTUAL switch this update (`changed.itemType === true` AND the previous
   * itemType, read off `lastData`, differs from the new one) -- never for the first resolve of a
   * freshly dropped block (`lastData` is `null` then, so there is nothing to have switched FROM),
   * and never for loading already-saved data where itemType did not change this update. In both of
   * those non-switch cases, `validItems`'s existing static-list filtering below still runs
   * unchanged -- collections still cannot be validated here, and that safety net for categories is
   * untouched.
   *
   * Normalizing shape: `validItems` deliberately accepts bare strings as well as `{ value }` rows
   * (hand-edited JSON may hold either), but Puck's array field expects rows, and its own item editor
   * typically writes back via `{ ...row, value: newValue }` -- spreading a bare STRING spreads its
   * characters into numeric keys instead of updating `.value`, corrupting the row the moment an
   * author edits it. So a row shape check has to be independent of whether anything was dropped: a
   * bare-string payload where every item happens to still be valid must still be rewritten, even
   * though `kept.length` never changes for that case. Found in review of ECOMM-13238 -- the original
   * short-circuit only compared counts, so an all-valid bare-string payload took the `return { props }`
   * branch and was never normalized.
   */
  resolveData: ({ props }, { changed, lastData }) => {
    const previousItemType = lastData ? (lastData.props as { itemType?: string }).itemType : undefined;
    const isTypeSwitch = lastData !== null && changed.itemType === true && previousItemType !== props.itemType;

    const kept = isTypeSwitch ? [] : validItems(props.itemType, props.items);
    const inputLength = Array.isArray(props.items) ? props.items.length : 0;
    // Reshaping only makes sense when `items` is actually an array to reshape -- a non-array value
    // (e.g. undefined, before defaultProps applies) has nothing to normalize, and `kept` is already
    // `[]` for it, so `needsReshape` stays false and the original short-circuit behaviour for that
    // case is unchanged.
    // `props.items` is declared as `NavItem[]`, so TypeScript believes every element is already an
    // object and the `!== null` check below is unreachable -- but saved Puck data is unvalidated
    // JSON, and hand-editing it (or a pre-migration payload) can genuinely hold a bare string or
    // `null` where the type promises a `{ value }` row. The declared type is a lie at this boundary;
    // the runtime check stays.
    const needsReshape =
      Array.isArray(props.items) &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime disagrees with the declared type; see comment above
      !props.items.every((item) => typeof item === 'object' && item !== null && 'value' in item);
    if (kept.length === inputLength && !needsReshape) {
      return { props };
    }
    return { props: { ...props, items: kept.map((value) => ({ value })) } };
  },
  resolveFields: (data, { fields }) => {
    const { items: _items, ...baseFields } = fields;
    const { itemType } = data.props;
    if (itemType === '') {
      return baseFields as Fields<NavCarouselProps>;
    }
    return ({
      ...baseFields,
      items: {
        type: 'array',
        label: itemType === 'collections' ? 'Collections (in order)' : 'Items (in order)',
        // Reordering is Puck's own array control, which is also what makes the author's order the
        // stored order.
        getItemSummary: (row: { value: string }, index?: number) =>
          row.value === '' ? `Item ${(index ?? 0) + 1}` : row.value,
        defaultItemProps: { value: '' },
        arrayFields: {
          value:
            itemType === 'collections'
              ? { type: 'text', label: 'Collection slug' }
              : // `selectField` derives its `key` from the option values, which is what makes a
                // resolveFields-rebuilt select actually re-render with the new list instead of
                // serving the previous type's closure.
                selectField(
                  'Category',
                  [{ label: 'Select…', value: '' }, ...itemOptionsFor(itemType)],
                  'Which item this tile links to.'
                ),
        },
      },
    } as unknown) as Fields<NavCarouselProps>;
  },
  render: NavCarouselRender,
};
